import React, { useEffect, useMemo, useRef, useState } from "react";
import { displayName } from "@/lib/names";
import { PersonAvatar } from "@/components/PersonAvatar";
import { useLang } from "@/context/LanguageContext";
import { Maximize2 } from "lucide-react";
import { buildLayout, NODE_W, NODE_H, V_GAP } from "@/lib/treeLayout";

const PAD = 40;
const LABEL_W = 120;
const STATUS_COLOR = { cross_validated: "#2C4230", testimony_confirmed: "#3E6B8A" };

const Node = ({ p, pos, color, clanName, onClick }) => (
  <g transform={`translate(${pos.x + LABEL_W + PAD}, ${pos.y + PAD})`} onClick={() => onClick?.(p)}
    style={{ cursor: "pointer" }} data-testid={`tree-node-${p.id}`} className="tree-node">
    <title>{`${displayName(p)} — voir la fiche`}</title>
    <rect width={NODE_W} height={NODE_H} rx={10} fill="#FAF7F2" stroke={color} strokeWidth={2}
      strokeDasharray={p.is_provisional ? "6 4" : undefined} />
    <circle cx={NODE_W - 10} cy={10} r={5} fill={STATUS_COLOR[p.verification_status] || "#D9822B"} />
    <foreignObject x={8} y={8} width={NODE_W - 16} height={NODE_H - 16}>
      <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PersonAvatar person={p} size={38} />
          <div style={{ overflow: "hidden" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 15, color: "#261911", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
              {displayName(p)}
            </div>
            <div style={{ fontSize: 10, color: "#6E5B4F", letterSpacing: "0.05em" }}>
              {p.birth_year ? `${p.birth_year_approx ? "vers " : ""}${p.birth_year}` : "?"}{p.death_year ? ` – ${p.death_year}` : (p.is_alive === false ? " – ?" : "")}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 6, fontSize: 9.5, color, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {p.is_provisional ? "Provisoire · " : ""}{clanName}
        </div>
      </div>
    </foreignObject>
  </g>
);

export const TreeView = ({ persons, clans, onPersonClick }) => {
  const { t } = useLang();
  const boxRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [showMaternal, setShowMaternal] = useState(true);
  const layout = useMemo(() => buildLayout(persons), [persons]);
  const clanOf = (key) => (clans || []).find(c => c.key === key);
  const vbWidth = layout.width + LABEL_W + PAD * 2;
  const vbHeight = layout.height + PAD * 2;

  const fit = () => {
    const w = boxRef.current?.clientWidth || vbWidth;
    setZoom(Math.max(0.3, Math.min(1, (w - 16) / vbWidth)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (window.innerWidth < 768) { fit(); setZoom(z => Math.max(z, 0.55)); } }, [vbWidth]);
  useEffect(() => {
    const b = boxRef.current;
    if (b) b.scrollLeft = (b.scrollWidth - b.clientWidth) / 2;
  }, [zoom, vbWidth]);

  if (!persons.length) {
    return (
      <div className="heritage-card p-10 text-center text-[#6E5B4F]" data-testid="empty-tree">
        <p className="font-serif-heritage text-xl mb-2">{t("emptyTree")}</p>
      </div>
    );
  }

  const ox = LABEL_W + PAD, oy = PAD;
  const btn = "w-8 h-8 rounded-full border border-[#E3D5CA] bg-[#FAF7F2] text-[#3A2312] inline-flex items-center justify-center";

  return (
    <div className="relative" data-testid="tree-view">
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} className={btn} data-testid="zoom-out-btn">−</button>
        <span className="text-xs text-[#6E5B4F] w-12 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className={btn} data-testid="zoom-in-btn">+</button>
        <button onClick={fit} className={btn} title="Ajuster à l'écran" data-testid="zoom-fit-btn"><Maximize2 className="w-4 h-4" /></button>
        <label className="flex items-center gap-2 text-sm text-[#6E5B4F] ml-2">
          <input type="checkbox" checked={showMaternal} onChange={e => setShowMaternal(e.target.checked)} data-testid="maternal-links-toggle" />
          {t("showMaternalLinks")}
        </label>
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-[#6E5B4F] mb-3" data-testid="tree-legend">
        <span className="flex items-center gap-2"><span className="w-8 border-t-2 border-[#B8860B]" />Union (couple)</span>
        <span className="flex items-center gap-2"><span className="w-8 border-t-2 border-[#993D22]" />Lien parents → enfants</span>
        {showMaternal && <span className="flex items-center gap-2"><span className="w-8 border-t border-dashed border-[#6E5B4F]" />{t("maternalLink")} seul</span>}
        <span className="flex items-center gap-2"><span className="w-6 h-4 rounded border-2 border-dashed border-[#A96B18]" />Fiche provisoire</span>
      </div>
      <div ref={boxRef} className="overflow-auto border border-[#E3D5CA] rounded-xl bg-[#FAF7F2] p-2" style={{ maxHeight: "75vh" }}>
        <svg width={vbWidth * zoom} height={vbHeight * zoom} viewBox={`0 0 ${vbWidth} ${vbHeight}`} style={{ display: "block" }}>
          {layout.rows.map(r => (
            <g key={r.gen} data-testid={`tree-row-${r.gen}`}>
              <line x1={PAD} x2={vbWidth - PAD} y1={oy + r.y - V_GAP / 2 + 4} y2={oy + r.y - V_GAP / 2 + 4} stroke="#E3D5CA" strokeDasharray="2 6" />
              <foreignObject x={PAD - 30} y={oy + r.y + NODE_H / 2 - 14} width={LABEL_W + 20} height={30}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 600, color: "#6B4226" }}>
                  Génération {r.gen}
                </div>
              </foreignObject>
            </g>
          ))}
          {layout.childLinks.filter(l => showMaternal || l.kind !== "maternal").map(l => {
            const c = layout.positions[l.to];
            const x2 = c.x + NODE_W / 2 + ox, y2 = c.y + oy, x1 = l.x1 + ox, y1 = l.y1 + oy;
            const midY = y2 - V_GAP / 2;
            return <path key={`c-${l.to}`} d={`M ${x1} ${y1} V ${midY} H ${x2} V ${y2}`}
              className={`tree-link tree-link-${l.kind === "maternal" ? "maternal" : "paternal"}`} data-testid={`child-link-${l.to}`} />;
          })}
          {layout.unions.map(u => {
            const a = layout.positions[u.a], b = layout.positions[u.b];
            const [l, r] = a.x <= b.x ? [a, b] : [b, a];
            if (u.adjacent) {
              const y = l.y + oy + NODE_H / 2, x1 = l.x + NODE_W + ox, x2 = r.x + ox;
              return (
                <g key={`u-${u.a}-${u.b}`} data-testid={`union-${u.a}-${u.b}`}>
                  <line x1={x1} x2={x2} y1={y} y2={y} stroke="#B8860B" strokeWidth={2.5} />
                  <circle cx={(x1 + x2) / 2} cy={y} r={4.5} fill="#FAF7F2" stroke="#B8860B" strokeWidth={2} />
                </g>
              );
            }
            const x1 = l.x + NODE_W / 2 + ox, x2 = r.x + NODE_W / 2 + ox, y1 = l.y + oy, y2 = r.y + oy;
            const top = Math.min(y1, y2) - 22;
            return <path key={`u-${u.a}-${u.b}`} d={`M ${x1} ${y1} C ${x1} ${top}, ${x2} ${top}, ${x2} ${y2}`}
              fill="none" stroke="#B8860B" strokeWidth={1.8} strokeDasharray="6 4" data-testid={`union-${u.a}-${u.b}`} />;
          })}
          {Object.entries(layout.positions).map(([id, pos]) => {
            const p = layout.byId[id];
            const clan = clanOf(p.clan_key);
            return <Node key={id} p={p} pos={pos} color={clan?.color || "#6B4226"} clanName={clan?.name || ""} onClick={onPersonClick} />;
          })}
        </svg>
      </div>
    </div>
  );
};

export default TreeView;
